import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { cooperplaceApi } from 'services/api';
import { PreLoadProvider } from './context';
import ApplicationLayout from 'v3/layouts/Application';
import { Button, Card, Text } from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import { convertNumberToDatabase, currencyOptions } from 'v3/utils/formatter';
import { DELIVERY_TYPE_OPTIONS } from 'v3/utils/constants';
import {
  fetchBodies,
  fetchCountries,
  fetchDispositionData,
  fetchImplements,
  fetchVehiclesTypes,
} from 'utils/fetches';

import {
  DataSchema
} from '../validator';
import { LoadData } from './components/LoadData';
import { VehicleData } from './components/VehicleData';
import { OriginData } from './components/OriginData';
import { DestinationsData } from './components/DestinationsData';

export function RegisterPreLoad() {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const params = useParams();

  const [data, setData] = useState(old => ({
    ...old,
    loadPublish: true,
    searchDriver: true,
    searchDriverRadius: 500,
    currency: currencyOptions[0],
    loadCreationNumber: 1,
    isTracked: false,
    dueDate: moment().add(1, 'days').format('YYYY-MM-DDTHH:mm'),
    origin: {
      country: {
        id: 1,
        name: 'Brasil',
        abbreviation: 'br',
        ddi: 55,
      },
    },
    destinations: [
      {
        type: DELIVERY_TYPE_OPTIONS[1],
        country: {
          id: 1,
          name: 'Brasil',
          abbreviation: 'br',
          ddi: 55,
        },
        index: 0,
      },
    ],
  }))

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [destinationsError, setDestinationsError] = useState({});
  const [loadDispositionOptions, setLoadDispositionOptions] = useState([]);
  const [vehicleTypesOptions, setVehicleTypesOptions] = useState([]);
  const [vehicleBodyTypesOptions, setVehicleBodyTypesOptions] = useState([]);
  const [vehicleImplementTypesOptions, setVehicleImplementTypesOptions] =
    useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);

  async function fetchOptions() {
    try {
      setLoading(true);
      let [
        vehicleTypesData,
        vehiclesBodyTypesData,
        implementsData,
        dispositionData,
        countriesData,
      ] = await Promise.all([
        fetchVehiclesTypes(),
        fetchBodies(),
        fetchImplements(),
        fetchDispositionData(),
        fetchCountries(),
      ]);
      vehicleTypesData = vehicleTypesData.filter(
        vtype => vtype.name !== 'Dolly' && vtype.name !== 'Semi Reboque'
      );
      setVehicleTypesOptions(vehicleTypesData);
      setVehicleBodyTypesOptions(vehiclesBodyTypesData);
      implementsData = implementsData.filter(vtype => vtype.name !== 'Dolly');
      setVehicleImplementTypesOptions(implementsData);
      setLoadDispositionOptions(dispositionData);
      setCountryOptions(countriesData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (
      params.clone_id &&
      countryOptions.length > 0
    ) {
      duplicatePreLoad(params.clone_id);
    }
  }, [params.clone_id, countryOptions]);


  function validateDestinationsStartDate(destinations) {
    if (!destinations || destinations.length <= 1) return true;
    let invalidsDates = [];

    for (let i = 0; i < destinations.length - 1; i++) {
      const nextStartScheduleTime = moment(
        destinations[i + 1].startSchedule,
        'YYYY-MM-DDTHH:mm'
      );
      const currentScheduledTime = moment(
        destinations[i].startSchedule,
        'YYYY-MM-DDTHH:mm'
      );

      if (currentScheduledTime >= nextStartScheduleTime) {
        invalidsDates.push(i);
      }
    }

    return invalidsDates;
  }

  function validateDestinationsEndDate(destinations) {
    if (!destinations || destinations.length <= 1) return true;
    let invalidsDates = [];

    for (let i = 1; i < destinations.length; i++) {
      const previousScheduledTime = moment(
        destinations[i - 1].date,
        'YYYY-MM-DDTHH:mm'
      );
      const currentScheduledTime = moment(
        destinations[i].date,
        'YYYY-MM-DDTHH:mm'
      );
      if (currentScheduledTime <= previousScheduledTime) {
        invalidsDates.push(i);
      }
    }

    return invalidsDates;
  }

  function validateDestinationDate(destinations) {
    if (!destinations || destinations.length <= 1) return true;
    let invalidsDates = [];
    let destArray = destinations.slice(0, destinations?.length - 1);

    for (let i = destinations.length - 1; i > 0; i--) {
      const invalidDate = destArray.some(item => {
        const previousScheduledTime = moment(item.date, 'YYYY-MM-DDTHH:mm');
        const currentScheduledTime = moment(
          destinations[i].startSchedule,
          'YYYY-MM-DDTHH:mm'
        );
        return previousScheduledTime > currentScheduledTime;
      });
      if (invalidDate) {
        invalidsDates.push(i);
      }
      destArray = destArray.slice(0, destArray?.length - 1);
    }
    return invalidsDates;
  }

  function validateDestinationDateWithOrigin(origin, destinations) {
    let invalidsEndDates = [];
    let invalidsStartDates = [];
    const originStart = moment(origin.startSchedule, 'YYYY-MM-DDTHH:mm');
    const originEnd = moment(origin.date, 'YYYY-MM-DDTHH:mm');
    destinations.forEach((dest, idx) => {
      const currentStartScheduledTime = moment(
        dest.startSchedule,
        'YYYY-MM-DDTHH:mm'
      );
      const currentEndTime = moment(dest.date, 'YYYY-MM-DDTHH:mm');
      if (currentStartScheduledTime < originStart) {
        invalidsStartDates.push(idx);
      }
      if (currentEndTime < originEnd) {
        invalidsEndDates.push(idx);
      }
    });
    return { start: invalidsStartDates, end: invalidsEndDates };
  }

  function validateAllDates(dataToValidate) {
    setDestinationsError({});
    const invalidsEndDate = validateDestinationsEndDate(
      dataToValidate?.destinations
    );
    const invalidsStartScheduleDate = validateDestinationsStartDate(
      dataToValidate?.destinations
    );
    const invalidEndStartDate = validateDestinationDate(
      dataToValidate?.destinations
    );
    const { start: invalidStart, end: invalidEnd } =
      validateDestinationDateWithOrigin(
        dataToValidate.origin,
        dataToValidate.destinations
      );

    let invalidsMessages = {};
    if (invalidsEndDate.length > 0) {
      invalidsEndDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.date`] =
          'Data máxima não pode ser inferior/igual ao destino anterior';
      });

      setDestinationsError(invalidsMessages);
    }
    if (invalidsStartScheduleDate.length > 0) {
      invalidsStartScheduleDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.startSchedule`] =
          'Data início não pode ser superior/igual ao destino posterior';
      });

      setDestinationsError(old => ({ ...old, ...invalidsMessages }));
    }
    if (invalidEndStartDate.length > 0) {
      invalidEndStartDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.startSchedule`] =
          'Data início entrega não pode ser menor que data máxima destino anterior';
      });
      setDestinationsError(old => ({ ...old, ...invalidsMessages }));
    }
    if (invalidStart?.length > 0) {
      invalidStart.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.startSchedule`] =
          'Data início entrega não pode ser menor que data da origem';
      });
      setDestinationsError(old => ({ ...old, ...invalidsMessages }));
    }
    if (invalidEnd?.length > 0) {
      invalidEnd.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.date`] =
          'Data máxima entrega não pode ser menor que data da origem';
      });
      setDestinationsError(old => ({ ...old, ...invalidsMessages }));
    }

    if (Object.keys(invalidsMessages)?.length > 0) {
      return false;
    }
    return true;
  }

  async function validate(dataToValidate, setErrors) {
    const errorList = {};
    setDestinationsError({});
    try {
      const areLocationsDatesValid = validateAllDates(dataToValidate);

      await DataSchema.validate(dataToValidate, { abortEarly: false });

      setErrors({ ...errorList });
      if (!areLocationsDatesValid) {
        return false;
      }
      return true;
    } catch (err) {
      if (err.inner) {
        err.inner.forEach(error => {
          errorList[error.path] = error.message;
        });
        setErrors({ ...errorList });
        return false;
      }
    }
  }

  const duplicatePreLoad = async (loadId) => {

    try {
      setLoadingDuplicate(true);
      setErrors({});

      const { data } = await cooperplaceApi.get(`pre-loads/${loadId}`);

      const loadOrigin = data?.loadOrigins[0]
      const isTracked = (data?.tracking === "1" || data?.monitoring === "1") ? true : false;

      const originList = {
        province: loadOrigin?.province,
        city: loadOrigin?.cityModel?.name,
        date: moment(loadOrigin?.scheduledTime).format('YYYY-MM-DDTHH:mm'),
        startSchedule: moment(loadOrigin?.startSchedule).format('YYYY-MM-DDTHH:mm'),
        cityModel: {
          cityId: loadOrigin?.cityModel?.id || null,
          city: loadOrigin?.cityModel?.name,
          name: loadOrigin?.cityModel?.name,
          lat: loadOrigin?.cityModel?.latitude || null,
          lng: loadOrigin?.cityModel?.longitude || null,
        },
        lat: loadOrigin?.cityModel?.latitude || null,
        lng: loadOrigin?.cityModel?.longitude || null,
        address: loadOrigin?.address,
        country: countryOptions.find(item => item?.abbreviation?.toLowerCase() === loadOrigin?.countrySlug?.toLowerCase()) || null,
        countrySlug: loadOrigin?.countrySlug,
      };

      const destinationList = data?.loadDestinations.map((dest, index) => {
        return {
          province: dest?.province,
          date: moment(dest?.scheduledTime).format('YYYY-MM-DDTHH:mm'),
          startSchedule: moment(dest?.startSchedule).format('YYYY-MM-DDTHH:mm'),
          cityModel: {
            cityId: dest?.cityModel?.id,
            city: dest?.cityModel?.name,
            name: dest?.cityModel?.name,
            lat: dest?.cityModel?.latitude || null,
            lng: dest?.cityModel?.longitude || null,
          },
          lat: dest?.cityModel?.latitude || null,
          lng: dest?.cityModel?.longitude || null,
          address: dest?.address,
          country: countryOptions.find(item => item?.abbreviation?.toLowerCase() === dest?.countrySlug?.toLowerCase()) || null,
          countrySlug: dest?.countrySlug,
          type: DELIVERY_TYPE_OPTIONS.find(item => item?.id === dest?.action) || null,
          index: index
        }
      })

      setData(old => ({
        ...old,
        totalDistanceInKm: data?.totalDistanceInKm,
        taker: {
          id: data?.taker?.id,
          social_name: data?.taker?.socialName,
          cgccpf: data?.taker?.cpfCnpj,
        },
        product: data?.product ?
          {
            id: data?.product?.id,
            name: data?.product?.name,
            product_type_name: data?.product?.productType?.name,
            product_type_id: data?.product?.productType?.id,
            ncm: data?.product?.ncm,

          } : null,
        costCenter: data?.costCenter,
        pallets: +data?.palletNumber,
        weight: +data?.loadWeight,
        dueDate: moment(new Date()).add(1, 'days').format('YYYY-MM-DDTHH:mm'),
        searchDriver: data?.searchDriver === 1 ? true : false,
        searchDriverRadius: data?.searchDriverRadius,
        vehicleTypes: data?.vehicleTypes || [],
        vehicleImplements: data?.vehicleImplementTypes || [],
        vehicleBodies: data?.vehicleBodyTypes || [],
        loadPublish: data?.loadPublish,
        isTracked: isTracked,
        riskManager: (isTracked || data?.riskManagers?.length > 0) ? data?.riskManagers[0] ? data?.riskManagers[0] : null : null,
        observations: data?.comments,
        origin: originList,
        destinations: destinationList,
      }));

    } catch (err) {
      setLoadingDuplicate(false);
      snackbar.show(<Text>Erro ao recuperar informações da carga!</Text>, {
        type: 'error'
      });
    } finally {
      setLoadingDuplicate(false);
    }
  }

  async function handleCreation() {
    setLoading(true);
    if (!(await validate(data, setErrors))) {
      snackbar.show(
        <Text type="body" weight={500}>
          Verifique por campos obrigatórios ou datas inválidas!
        </Text>,
        {
          type: 'error',
        }
      );
      setLoading(false);
      return false;
    }

    const originList = {
      province: data?.origin?.province,
      scheduledTime: moment(data.origin?.date, 'YYYY-MM-DDTHH:mm'),
      cityId: data?.origin?.cityModel?.cityId || data?.origin?.cityModel?.id,
      city: data?.origin?.cityModel?.name,
      lat: data?.origin?.cityModel?.lat,
      lng: data?.origin?.cityModel?.lng,
      address: data?.origin?.cityModel?.name,
      countrySlug:
        data?.origin?.countrySlug || data?.origin?.country?.abbreviation,
      startSchedule: moment(data?.origin.startSchedule, 'YYYY-MM-DDTHH:mm'),
    };

    const destinationList = data.destinations.map(dest => (
      {
        province: dest?.province,
        scheduledTime: moment(dest?.date, 'YYYY-MM-DDTHH:mm'),
        cityId: dest?.cityModel?.cityId || dest?.cityModel?.id,
        city: dest?.cityModel?.name,
        lat: dest?.lat,
        lng: dest?.lng,
        address: dest?.cityModel?.name,
        countrySlug: dest?.countrySlug || dest?.country?.abbreviation,
        action: dest?.type?.id,
        startSchedule: moment(dest?.startSchedule, 'YYYY-MM-DDTHH:mm'),
      }));

    const payload = {
      totalDistanceInKm: data?.totalDistanceInKm,
      personClientId: data?.taker?.id,
      productId: data?.product?.id,
      costCenterId: data?.costCenter?.id,
      takerValue: +data?.takerValue,
      pallets: +data?.pallets,
      loadWeight: +convertNumberToDatabase(data?.weight),
      dueDate: data?.dueDate,
      searchDriver: data?.searchDriver ? 1 : 0,
      searchDriverRadius: data?.searchDriver
        ? data?.searchDriverRadius
          ? Number(data?.searchDriverRadius)
          : 500
        : 0,
      vehicleTypeIds: data?.vehicleTypes?.map(type => type.id),
      vehicleImplementTypeIds: data?.vehicleImplements
        ? data?.vehicleImplements.map(implement => implement.id)
        : [],
      vehicleBodyTypeIds: data?.vehicleBodies?.map(body => body.id),
      loadOrigins: originList,
      loadDestinations: destinationList,
      clientOrderNumber: data?.clientOrderNumber || null,
      loadPublish: data?.loadPublish,
      isTracked: data?.isTracked,
      riskManagerId: data?.isTracked ? data?.riskManager?.id : null,
      comments: data?.observations,
    };

    try {
      for (let i = 0; i < data?.loadCreationNumber; i++) {
        await cooperplaceApi.post('pre-loads', payload);
      }
      snackbar.show(<Text>Pré carga cadastrada com sucesso!</Text>, {
        type: 'success',
      });

      setLoading(false);
      navigate(`/pre-cargas`);
    } catch (error) {
      let message =
        error?.response?.data?.message ?? 'Erro ao cadastrar pré carga!';
      snackbar.show(<Text>{message}</Text>, {
        type: 'error',
      });
      setLoading(false);
    }
  }

  function handleCancel() {
    setErrors({});
    setData({});
    navigate(`/pre-cargas`);
  }

  return (
    <PreLoadProvider
      value={{
        data,
        setData,
        loading,
        setLoading,
        errors,
        setErrors,
        snackbar,
        vehicleTypesOptions,
        vehicleBodyTypesOptions,
        vehicleImplementTypesOptions,
        loadDispositionOptions,
        countryOptions,
        destinationsError,
        loadingDuplicate,
      }}
    >
      <ApplicationLayout
        title={'Cadastro de pré carga'}
        RightComponent={
          <>
            <Button
              variant={'secondary'}
              onClick={() => handleCancel()}
              loading={loading}
            >
              <Text weight={500}>Cancelar</Text>
            </Button>
            <Button
              variant={'primary'}
              onClick={() => handleCreation()}
              loading={loading}
            >
              <Text weight={500}>Salvar</Text>
            </Button>
          </>
        }
      >
        <Card
          bodyCardPadding="1rem"
        >
          <LoadData />
          <VehicleData />
          <OriginData />
          <DestinationsData />
        </Card>
      </ApplicationLayout>
    </PreLoadProvider>
  );
}
